// src/pages/dashboard/Menu.jsx
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import StageMenuItem from './menu/StageMenuItem';
import ProjectTypePicker from './projectPage/ProjectTypePicker';
import { AppContext } from '../../context/AppContext';
import { urlMap, getAuthHeaders } from '../../utils';

import pencilIcon from '../../assets/icons/pencil.svg';
import playIcon from '../../assets/icons/play-circle.svg';
import sparklesIcon from '../../assets/icons/sparkles.svg';
import socialMediaIcon from '../../assets/icons/social-media.svg';
import webIcon from '../../assets/icons/web.svg';
import uploadIcon from '../../assets/icons/upload.svg';
import slidersIcon from '../../assets/icons/sliders.svg';

export default class Menu extends Component {
  static contextType = AppContext;

  state = {
    search: '',
    disabledMenuInfo: '',
  };

  disabledInfoTimer = null;

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    if (this.disabledInfoTimer) clearTimeout(this.disabledInfoTimer);
  }

  handleKeyDown = (e) => {
    const { open, onClose } = this.props;
    if (e.key === 'Escape' && open) onClose?.();
  };

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
  };

  showDisabledMenuInfo = (message) => {
    if (this.disabledInfoTimer) clearTimeout(this.disabledInfoTimer);
    this.setState({ disabledMenuInfo: String(message || 'This menu item is currently unavailable.') });
    this.disabledInfoTimer = setTimeout(() => {
      this.setState({ disabledMenuInfo: '' });
    }, 3000);
  };

  getStageIcon = (stage) => ({
    project_stage: pencilIcon,
    reading: playIcon,
    showing: sparklesIcon,
    publishing: uploadIcon,
    social_media: socialMediaIcon,
    editing: slidersIcon,
  }[stage] || pencilIcon);

  getStageLabel = (stage) => ({
    project_stage: 'Editor',
    reading: 'Reader',
    showing: 'Decorator',
    publishing: 'Publisher',
    social_media: 'Social Media',
    editing: 'Website Builder',
  }[stage] || stage);

  updateProjectType = async (patch) => {
    const project = this.context?.state?.currentProject;
    if (!project) throw new Error('NO_PROJECT');
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const res = await fetch(`${(urlMap?.projectEndpoint || '/core/projects')}/${project.id}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload?.error || `HTTP_${res.status}`);
    }
    const updated = await res.json();
    this.context?.setCurrentProject?.(updated);
    return updated;
  };

  render() {
    const { activeTab, onTabChange, open, onClose, className = '' } = this.props;
    const { search, disabledMenuInfo } = this.state;
    const project = this.context?.state?.currentProject;

    const defaultItems = [
      { label: 'Editor', stage: 'project_stage', icon: pencilIcon },
      { label: 'Social Media', stage: 'social_media', icon: socialMediaIcon },
      { label: 'Reader', stage: 'reading', icon: playIcon },
      { label: 'Decorator', stage: 'showing', icon: sparklesIcon },
      { label: 'Publisher', stage: 'publishing', icon: uploadIcon },
      { label: 'Website Builder', stage: 'web', icon: webIcon }
    ];

    const query = search.trim().toLowerCase();
    const items = query
      ? defaultItems.filter(({ label }) => label.toLowerCase().includes(query))
      : defaultItems;

    return (
      <>
        <div
          className={`menu-scrim ${open ? 'is-open' : ''}`}
          onClick={onClose}
          aria-hidden="true"
        />

        <nav
          id="app-sidebar"
          className={`menu ${open ? 'menu--open' : ''} ${className}`.trim()}
          aria-label="Project navigation"
        >
          <Link to="/home" className="menu-item menu-home-link" onClick={onClose}>
            ← Home
          </Link>

          {project && (
            <div className="menu-project-type">
              <ProjectTypePicker
                project={project}
                disabled={!!project.archived_at}
                onApplyPatch={this.updateProjectType}
                onApplied={(updatedProject, selectedType) => {
                  this.context?.setCurrentProject?.(updatedProject);
                  if (selectedType) this.context?.setProjectType?.(selectedType);
                }}
              />
            </div>
          )}

          <div className="menu-search">
            <input
              type="text"
              className="menu-search-input"
              placeholder="Search agents"
              value={search}
              onChange={this.handleSearchChange}
              aria-label="Search agents"
            />
          </div>

          <ul className="menu-list">
            {items.length === 0 ? (
              <li className="menu-empty">No agents found</li>
            ) : (
              items.map(({ label, icon }) => (
                <li key={label}>
                  <StageMenuItem
                    label={label}
                    icon={icon}
                    isActive={activeTab === label}
                    isDisabled={label !== 'Editor' && label !== 'Social Media'}
                    disabledReason={`${label} is in development and will be available soon.`}
                    onDisabledInfo={this.showDisabledMenuInfo}
                    onClick={onTabChange}
                    onClose={onClose}
                  />
                </li>
              ))
            )}
          </ul>

          {disabledMenuInfo && (
            <p className="menu-disabled-info" role="status" aria-live="polite">{disabledMenuInfo}</p>
          )}
        </nav>
      </>
    );
  }
}
